import React, { useEffect, useState } from 'react';
import {
  Bookmark, BookmarkCheck, Plus, Save, Pencil, Trash2,
  Loader2, X, Check, AlertCircle,
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

/**
 * Saved search profile selector for the Properties page.
 *
 * - Lists profiles, highlights the active one
 * - Click a profile → activate it (server records is_active, parent
 *   reads filters via onActivate callback)
 * - "Save current filters as a new profile" creates from current state
 * - "All Stock" deactivates any active profile (back to global view)
 * - Inline rename + delete on hover
 */
export default function SearchProfileSelector({ currentFilters, currentWeights, onActivate, onCleared }) {
  const [profiles, setProfiles] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  const refresh = async () => {
    try {
      const r = await api.get('/api/account/search-profiles');
      setProfiles(r.data || []);
    } catch {
      setProfiles([]);
    }
  };

  useEffect(() => { refresh(); }, []);

  const activate = async (id) => {
    try {
      const r = await api.post(`/api/account/search-profiles/${id}/activate`);
      toast.success(`Switched to "${r.data.name}"`);
      await refresh();
      if (onActivate) onActivate(r.data);
    } catch {
      toast.error("Couldn't activate that profile");
    }
  };

  const clearActive = async () => {
    try {
      await api.post('/api/account/search-profiles/deactivate');
      await refresh();
      if (onCleared) onCleared();
    } catch {/* ignore */}
  };

  const createFromCurrent = async () => {
    if (!newName.trim()) return;
    setSubmitting(true);
    try {
      const body = {
        name: newName.trim(),
        ...currentFilters,
        scoring_weights: currentWeights || null,
      };
      // Strip undefined / empty so the server gets clean payload
      Object.keys(body).forEach(k => {
        if (body[k] === undefined || body[k] === '' || body[k] === null) delete body[k];
      });
      body.name = newName.trim();
      const r = await api.post('/api/account/search-profiles', body);
      // Activate the new one immediately
      await api.post(`/api/account/search-profiles/${r.data.id}/activate`);
      toast.success(`Saved "${r.data.name}"`);
      setCreating(false);
      setNewName('');
      await refresh();
      if (onActivate) onActivate(r.data);
    } catch {
      toast.error('Could not save profile');
    } finally {
      setSubmitting(false);
    }
  };

  const rename = async (id) => {
    if (!editName.trim()) {
      setEditingId(null);
      return;
    }
    try {
      const profile = profiles.find(p => p.id === id);
      const body = { ...profile, name: editName.trim() };
      delete body.id; delete body.is_active; delete body.created_at; delete body.updated_at;
      await api.put(`/api/account/search-profiles/${id}`, body);
      toast.success('Renamed');
      await refresh();
    } catch {
      toast.error('Rename failed');
    } finally {
      setEditingId(null);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete profile "${name}"?`)) return;
    try {
      await api.delete(`/api/account/search-profiles/${id}`);
      toast.success('Profile deleted');
      await refresh();
    } catch {
      toast.error('Delete failed');
    }
  };

  if (profiles === null) {
    return null;
  }

  const noProfiles = profiles.length === 0;
  const activeProfile = profiles.find(p => p.is_active);

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <Bookmark size={16} className="text-emerald-400" /> Saved searches
        </h3>
        {!creating && (
          <button onClick={() => setCreating(true)}
            className="text-xs inline-flex items-center gap-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 rounded-lg px-2.5 py-1.5">
            <Plus size={12} /> Save current filters
          </button>
        )}
      </div>

      {noProfiles && !creating && (
        <p className="text-xs text-slate-500 leading-relaxed">
          You haven't set up any search profiles yet. Set the filters above (postcode, radius, beds, price, etc.)
          and your scoring sliders, then click "Save current filters" to keep them as a named profile —
          useful when you cover multiple areas with different criteria.
        </p>
      )}

      {creating && (
        <div className="flex items-center gap-2 p-2 bg-slate-800/50 rounded-lg">
          <input
            type="text"
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') createFromCurrent();
              if (e.key === 'Escape') { setCreating(false); setNewName(''); }
            }}
            placeholder="e.g. Manchester core, Leeds suburban, London commuter belt"
            className="flex-1 px-3 py-1.5 bg-slate-900 border border-slate-700 rounded-md text-sm text-slate-200 placeholder-slate-500"
          />
          <button onClick={createFromCurrent} disabled={!newName.trim() || submitting}
            className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-white text-xs font-semibold rounded-md inline-flex items-center gap-1.5">
            {submitting ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
          </button>
          <button onClick={() => { setCreating(false); setNewName(''); }}
            className="text-slate-500 hover:text-slate-300">
            <X size={14} />
          </button>
        </div>
      )}

      {!noProfiles && (
        <div className="flex flex-wrap gap-1.5">
          <button onClick={clearActive}
            className={`text-xs px-3 py-1.5 rounded-lg border ${
              !activeProfile
                ? 'bg-slate-700 border-slate-600 text-white'
                : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
            }`}>
            All Stock
          </button>
          {profiles.map(p => (
            <ProfileChip
              key={p.id}
              profile={p}
              editing={editingId === p.id}
              editName={editName}
              setEditName={setEditName}
              onActivate={() => activate(p.id)}
              onStartRename={() => { setEditingId(p.id); setEditName(p.name); }}
              onCommitRename={() => rename(p.id)}
              onCancelRename={() => setEditingId(null)}
              onDelete={() => remove(p.id, p.name)}
            />
          ))}
        </div>
      )}

      {activeProfile && (
        <p className="text-[11px] text-emerald-400/80 flex items-center gap-1.5">
          <BookmarkCheck size={11} /> Showing properties matching <strong>{activeProfile.name}</strong>
          {' '}— filters above reflect this profile.
        </p>
      )}
    </div>
  );
}


function ProfileChip({ profile, editing, editName, setEditName, onActivate, onStartRename, onCommitRename, onCancelRename, onDelete }) {
  if (editing) {
    return (
      <span className="inline-flex items-center gap-1 bg-slate-800 border border-emerald-500/40 rounded-lg px-2 py-1">
        <input
          autoFocus
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onCommitRename();
            if (e.key === 'Escape') onCancelRename();
          }}
          className="bg-transparent text-xs text-slate-100 outline-none w-32"
        />
        <button onClick={onCommitRename} className="text-emerald-400">
          <Check size={12} />
        </button>
        <button onClick={onCancelRename} className="text-slate-500">
          <X size={12} />
        </button>
      </span>
    );
  }
  return (
    <span className={`group inline-flex items-center text-xs border rounded-lg ${
      profile.is_active
        ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'
        : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
    }`}>
      <button onClick={onActivate} className="px-3 py-1.5">
        {profile.is_active && <BookmarkCheck size={11} className="inline mr-1" />}
        {profile.name}
      </button>
      <button onClick={onStartRename}
        className="px-1 py-1.5 opacity-0 group-hover:opacity-100 hover:text-slate-200">
        <Pencil size={11} />
      </button>
      <button onClick={onDelete}
        className="px-1 py-1.5 opacity-0 group-hover:opacity-100 hover:text-rose-400">
        <Trash2 size={11} />
      </button>
    </span>
  );
}
